import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { CollapsibleFilterHeader } from "@/components/ui/collapsible-filter-header";
import { ExpandCollapseButton } from "@/components/ui/expand-collapse-button";
import { CsvImportDialog, type NormalizedCsvPreview } from "@/components/ui/csv-import-dialog";
import {
  Search, Plus, Pencil, Trash2, MoreVertical,
  ArrowUp, ArrowDown, BookOpen, X, MapPin, Sprout,
  CheckCircle2, ArrowRightLeft,
  Loader2, FolderInput, Tag, Eye, EyeOff,
  ChevronDown
} from "lucide-react";

export interface GlossaryCategoryRecord {
  slug: string;
  label: string;
  createdAt: string;
  groupCount: number;
}

const LOCATION_CODES = ["BO", "SA", "ME", "CA"] as const;
const LOCATION_LABELS: Record<string, string> = {
  BO: "Boston",
  SA: "Salem",
  ME: "Methuen",
  CA: "Cape Cod",
};

interface GlossaryChild {
  id: string;
  parentId: string;
  value: string;
  aliases: string[];
  definition: string;
  spokenForm: string | null;
  sortOrder: number;
  isActive: boolean;
  observedAtLocations: string[];
  createdAt: string;
  updatedAt: string;
}

interface GlossaryTerm {
  id: string;
  term: string;
  category: string;
  definition: string;
  sortOrder: number;
  isActive: boolean;
  observedAtLocations: string[];
  createdAt: string;
  updatedAt: string;
  children: GlossaryChild[];
}

interface GlossaryStats {
  totalParents: number;
  totalChildren: number;
  activeParents: number;
  activeChildren: number;
}

function HighlightText({ text, search }: { text: string; search: string }) {
  if (!search || !text) return <>{text}</>;
  const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const splitRegex = new RegExp(`(${escaped})`, "gi");
  const testRegex = new RegExp(`^${escaped}$`, "i");
  const parts = text.split(splitRegex);
  return (
    <>
      {parts.map((part, i) =>
        testRegex.test(part) ? (
          <mark key={i} className="bg-yellow-200 dark:bg-yellow-800/50 rounded-sm px-0.5">{part}</mark>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </>
  );
}

function LocationBadges({ locations }: { locations: string[] }) {
  if (!locations || locations.length === 0) return <span className="text-xs text-muted-foreground">All locations</span>;
  return (
    <div className="flex items-center gap-1 flex-wrap">
      {locations.map((loc) => (
        <Badge key={loc} variant="outline" className="text-xs no-default-hover-elevate no-default-active-elevate">
          {loc}
        </Badge>
      ))}
    </div>
  );
}

function TermExtras({ child, searchTerm }: { child: GlossaryChild; searchTerm: string }) {
  const [expanded, setExpanded] = useState(false);
  const hasAliases = child.aliases.length > 0;
  const hasLocations = child.observedAtLocations && child.observedAtLocations.length > 0;
  const hasSpokenForm = !!child.spokenForm;
  if (!hasAliases && !hasLocations && !hasSpokenForm) return null;

  return (
    <div>
      {expanded ? (
        <div className="space-y-1 pt-1">
          {hasAliases && (
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-xs font-medium text-muted-foreground">Alias:</span>
              {child.aliases.map((alias) => (
                <Badge key={alias} variant="secondary" className="text-xs no-default-hover-elevate no-default-active-elevate">
                  <HighlightText text={alias} search={searchTerm} />
                </Badge>
              ))}
            </div>
          )}
          {hasLocations && (
            <div className="flex items-center gap-1 flex-wrap">
              <MapPin className="w-3 h-3 text-muted-foreground" />
              <LocationBadges locations={child.observedAtLocations} />
            </div>
          )}
          {hasSpokenForm && (
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">Staff say: </span>
              <span className="italic">"{child.spokenForm}"</span>
            </p>
          )}
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors pt-0.5"
            data-testid={`button-show-less-${child.id}`}
          >
            <ChevronDown className="w-3 h-3 rotate-180 transition-transform" />
            Show less
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors pt-1.5"
          data-testid={`button-show-more-${child.id}`}
        >
          <ChevronDown className="w-3 h-3 transition-transform" />
          Show more
        </button>
      )}
    </div>
  );
}

function TagInput({
  values,
  onChange,
  placeholder = "Type and press Enter or comma",
}: {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}) {
  const [inputValue, setInputValue] = useState("");

  function addValue(raw: string) {
    const trimmed = raw.trim();
    if (trimmed && !values.includes(trimmed)) {
      onChange([...values, trimmed]);
    }
    setInputValue("");
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if ((e.key === "Enter" || e.key === ",") && inputValue.trim()) {
      e.preventDefault();
      addValue(inputValue);
    } else if (e.key === "Backspace" && !inputValue && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    if (val.includes(",")) {
      const parts = val.split(",");
      const newValues = [...values];
      parts.forEach((part) => {
        const trimmed = part.trim();
        if (trimmed && !newValues.includes(trimmed)) {
          newValues.push(trimmed);
        }
      });
      if (newValues.length !== values.length) {
        onChange(newValues);
      }
      setInputValue("");
    } else {
      setInputValue(val);
    }
  }

  return (
    <div className="flex flex-wrap gap-1 p-2 border rounded-md min-h-9 items-center bg-background">
      {values.map((val) => (
        <Badge key={val} variant="secondary" className="text-xs gap-1">
          {val}
          <button
            type="button"
            onClick={() => onChange(values.filter((v) => v !== val))}
            className="ml-0.5 rounded-full"
            data-testid={`button-remove-tag-${val}`}
          >
            <X className="w-3 h-3" />
          </button>
        </Badge>
      ))}
      <input
        type="text"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        placeholder={values.length === 0 ? placeholder : ""}
        className="flex-1 min-w-[80px] bg-transparent outline-none text-sm"
        data-testid="input-tag"
      />
    </div>
  );
}

function LocationSelect({
  values,
  onChange,
}: {
  values: string[];
  onChange: (values: string[]) => void;
}) {
  const available = LOCATION_CODES.filter((c) => !values.includes(c));

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {values.map((loc) => (
          <Badge key={loc} variant="secondary" className="text-xs gap-1">
            {LOCATION_LABELS[loc] || loc}
            <button
              type="button"
              onClick={() => onChange(values.filter((v) => v !== loc))}
              className="ml-0.5 rounded-full"
              data-testid={`button-remove-location-${loc}`}
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
      {available.length > 0 && (
        <Select onValueChange={(val) => onChange([...values, val])}>
          <SelectTrigger data-testid="select-add-location">
            <SelectValue placeholder="Add location..." />
          </SelectTrigger>
          <SelectContent>
            {available.map((code) => (
              <SelectItem key={code} value={code} data-testid={`option-location-${code}`}>
                {LOCATION_LABELS[code]} ({code})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {values.length === 0 && (
        <p className="text-xs text-muted-foreground">No locations selected means all locations</p>
      )}
    </div>
  );
}

function quoteField(val: string): string {
  const escaped = val.replace(/"/g, '""');
  return `"${escaped}"`;
}

function exportGlossaryCsv(terms: GlossaryTerm[]) {
  const now = new Date();
  const timestamp = now.toISOString();
  const fileTimestamp = timestamp.replace(/[:.]/g, "").replace("T", "_").slice(0, 15);

  const headers = [
    "parent_id", "term", "category", "parent_definition", "parent_sort_order",
    "parent_locations", "parent_active", "child_id", "value", "aliases",
    "child_definition", "spoken_form", "child_sort_order", "child_locations",
    "child_active",
  ];

  const rows: string[] = [];
  rows.push(`#_exported_at=${timestamp}`);
  rows.push(headers.map(quoteField).join(","));

  const sorted = [...terms].sort((a, b) => a.sortOrder - b.sortOrder);
  for (const term of sorted) {
    const sortedChildren = [...term.children].sort((a, b) => a.sortOrder - b.sortOrder);
    for (const child of sortedChildren) {
      rows.push([
        quoteField(term.id),
        quoteField(term.term),
        quoteField(term.category),
        quoteField(term.definition),
        quoteField(String(term.sortOrder)),
        quoteField((term.observedAtLocations || []).join(";")),
        quoteField(String(term.isActive)),
        quoteField(child.id),
        quoteField(child.value),
        quoteField((child.aliases || []).join(";")),
        quoteField(child.definition || ""),
        quoteField(child.spokenForm || ""),
        quoteField(String(child.sortOrder)),
        quoteField((child.observedAtLocations || []).join(";")),
        quoteField(String(child.isActive)),
      ].join(","));
    }
  }

  const csvContent = rows.join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `glossary_export_${fileTimestamp}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

interface ImportPreview {
  preview: true;
  exportedAt: string | null;
  staleWarning: string | null;
  newParents: Array<{ term: string; category: string }>;
  updatedParents: Array<{ term: string; category: string }>;
  deletedParents: Array<{ id: string; term: string; category: string }>;
  newChildren: Array<{ parentTerm: string; value: string }>;
  updatedChildren: Array<{ parentTerm: string; value: string }>;
  movedChildren: Array<{ value: string; oldParentTerm: string; newParentTerm: string }>;
  deletedChildren: Array<{ id: string; parentTerm: string; value: string }>;
  errors: Array<{ row: number; message: string }>;
  deletionThresholdWarning: string | null;
}

interface ImportResult {
  committed: true;
  parentsCreated: number;
  parentsUpdated: number;
  parentsDeleted: number;
  childrenCreated: number;
  childrenUpdated: number;
  childrenMoved: number;
  childrenDeleted: number;
}

interface TermFormData {
  term: string;
  category: string;
  definition: string;
  observedAtLocations: string[];
}

interface ChildFormData {
  value: string;
  aliases: string[];
  definition: string;
  spokenForm: string;
  observedAtLocations: string[];
}

export interface CategoryItem {
  value: string;
  label: string;
  groupCount: number;
}

export interface GlossaryActions {
  openInstructions: () => void;
  exportCsv: () => void;
  importCsv: () => void;
  canExport: boolean;
}

export interface GlossaryTabProps {
  categoryFilter: string;
  setCategoryFilter: (slug: string) => void;
  categories: CategoryItem[];
  categoryMgmtOpen?: boolean;
  setCategoryMgmtOpen?: (open: boolean) => void;
  addGroupRef?: React.MutableRefObject<(() => void) | null>;
  actionsRef?: React.MutableRefObject<GlossaryActions | null>;
}

export function GlossaryTab({ categoryFilter, setCategoryFilter, categories, categoryMgmtOpen: externalCategoryMgmtOpen, setCategoryMgmtOpen: externalSetCategoryMgmtOpen, addGroupRef, actionsRef }: GlossaryTabProps) {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortOrder, setSortOrder] = useState<"display" | "az">("display");
  const [showInactive, setShowInactive] = useState(false);
  const [filterCollapsed, setFilterCollapsed] = useState(false);
  const [expandedTerms, setExpandedTerms] = useState<Set<string>>(new Set());

  const [termDialogOpen, setTermDialogOpen] = useState(false);
  const [editingTerm, setEditingTerm] = useState<GlossaryTerm | null>(null);
  const [termForm, setTermForm] = useState<TermFormData>({ term: "", category: "score", definition: "", observedAtLocations: [] });

  const [childDialogOpen, setChildDialogOpen] = useState(false);
  const [editingChild, setEditingChild] = useState<GlossaryChild | null>(null);
  const [childParentId, setChildParentId] = useState<string>("");
  const [childForm, setChildForm] = useState<ChildFormData>({ value: "", aliases: [], definition: "", spokenForm: "", observedAtLocations: [] });

  const [deleteTarget, setDeleteTarget] = useState<{ type: "term" | "child"; id: string; label: string; hasChildren?: number } | null>(null);

  const [moveChildTarget, setMoveChildTarget] = useState<{ childId: string; childValue: string; currentParentId: string } | null>(null);
  const [moveChildSelectedParent, setMoveChildSelectedParent] = useState("");
  const [changeCategoryTarget, setChangeCategoryTarget] = useState<{ termId: string; termName: string; currentCategory: string } | null>(null);
  const [changeCategorySelected, setChangeCategorySelected] = useState("");

  const [csvHelpOpen, setCsvHelpOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);

  const glossaryQuery = useQuery<{ terms: GlossaryTerm[] }>({
    queryKey: ["/api/glossary"],
  });

  const statsQuery = useQuery<GlossaryStats>({
    queryKey: ["/api/glossary/stats"],
  });

  const [internalCategoryMgmtOpen, setInternalCategoryMgmtOpen] = useState(false);
  const categoryMgmtOpen = externalCategoryMgmtOpen ?? internalCategoryMgmtOpen;
  const setCategoryMgmtOpen = externalSetCategoryMgmtOpen ?? setInternalCategoryMgmtOpen;
  const [newCatSlug, setNewCatSlug] = useState("");
  const [newCatLabel, setNewCatLabel] = useState("");
  const [editingCatSlug, setEditingCatSlug] = useState<string | null>(null);
  const [editCatLabel, setEditCatLabel] = useState("");
  const [editCatSlug, setEditCatSlug] = useState("");

  const addCategoryMutation = useMutation({
    mutationFn: (body: { slug: string; label: string }) =>
      apiRequest("POST", "/api/glossary/categories", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/glossary/categories"] });
      setNewCatSlug("");
      setNewCatLabel("");
      toast({ title: "Category added" });
    },
    onError: (err: Error) => {
      toast({ title: "Failed to add category", description: err.message, variant: "destructive" });
    },
  });

  const deleteCategoryMutation = useMutation({
    mutationFn: (slug: string) =>
      apiRequest("DELETE", `/api/glossary/categories/${slug}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/glossary/categories"] });
      toast({ title: "Category deleted" });
    },
    onError: (err: Error) => {
      toast({ title: "Cannot delete category", description: err.message, variant: "destructive" });
    },
  });

  const updateCategoryMutation = useMutation({
    mutationFn: (body: { originalSlug: string; label?: string; slug?: string }) =>
      apiRequest("PATCH", `/api/glossary/categories/${body.originalSlug}`, { label: body.label, slug: body.slug }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/glossary/categories"] });
      queryClient.invalidateQueries({ queryKey: ["/api/glossary"] });
      setEditingCatSlug(null);
      toast({ title: "Category updated" });
    },
    onError: (err: Error) => {
      toast({ title: "Failed to update category", description: err.message, variant: "destructive" });
    },
  });

  const seedMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/glossary/seed"),
    onSuccess: async (res) => {
      const data = await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/glossary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/glossary/stats"] });
      toast({ title: "Glossary seeded", description: `Created ${data.parentsCreated} concepts and ${data.childrenCreated} entries.` });
    },
    onError: () => toast({ title: "Seed failed", variant: "destructive" }),
  });

  const createTermMutation = useMutation({
    mutationFn: (data: TermFormData) => apiRequest("POST", "/api/glossary/terms", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/glossary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/glossary/stats"] });
      setTermDialogOpen(false);
      toast({ title: "Concept created" });
    },
    onError: () => toast({ title: "Failed to create concept", variant: "destructive" }),
  });

  const updateTermMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TermFormData> & { isActive?: boolean } }) =>
      apiRequest("PATCH", `/api/glossary/terms/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/glossary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/glossary/stats"] });
      setTermDialogOpen(false);
      setEditingTerm(null);
      toast({ title: "Concept updated" });
    },
    onError: () => toast({ title: "Failed to update concept", variant: "destructive" }),
  });

  const deleteTermMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/glossary/terms/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/glossary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/glossary/stats"] });
      setDeleteTarget(null);
      toast({ title: "Concept deleted" });
    },
    onError: () => toast({ title: "Failed to delete concept", variant: "destructive" }),
  });

  const createChildMutation = useMutation({
    mutationFn: (data: ChildFormData & { parentId: string }) => apiRequest("POST", "/api/glossary/children", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/glossary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/glossary/stats"] });
      setChildDialogOpen(false);
      toast({ title: "Entry created" });
    },
    onError: () => toast({ title: "Failed to create entry", variant: "destructive" }),
  });

  const updateChildMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ChildFormData> & { isActive?: boolean; sortOrder?: number } }) =>
      apiRequest("PATCH", `/api/glossary/children/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/glossary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/glossary/stats"] });
      setChildDialogOpen(false);
      setEditingChild(null);
      toast({ title: "Entry updated" });
    },
    onError: () => toast({ title: "Failed to update entry", variant: "destructive" }),
  });

  const moveChildMutation = useMutation({
    mutationFn: ({ childId, newParentId }: { childId: string; newParentId: string }) =>
      apiRequest("PATCH", `/api/glossary/children/${childId}`, { parentId: newParentId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/glossary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/glossary/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/glossary/categories"] });
      setMoveChildTarget(null);
      setMoveChildSelectedParent("");
      toast({ title: "Entry moved to new concept" });
    },
    onError: () => toast({ title: "Failed to move entry", variant: "destructive" }),
  });

  const changeCategoryMutation = useMutation({
    mutationFn: ({ termId, newCategory }: { termId: string; newCategory: string }) =>
      apiRequest("PATCH", `/api/glossary/terms/${termId}`, { category: newCategory }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/glossary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/glossary/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/glossary/categories"] });
      setChangeCategoryTarget(null);
      setChangeCategorySelected("");
      toast({ title: "Concept category changed" });
    },
    onError: () => toast({ title: "Failed to change category", variant: "destructive" }),
  });

  const deleteChildMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/glossary/children/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/glossary"] });
      queryClient.invalidateQueries({ queryKey: ["/api/glossary/stats"] });
      setDeleteTarget(null);
      toast({ title: "Entry deleted" });
    },
    onError: () => toast({ title: "Failed to delete entry", variant: "destructive" }),
  });

  const updateTermSortMutation = useMutation({
    mutationFn: ({ id, sortOrder }: { id: string; sortOrder: number }) =>
      apiRequest("PATCH", `/api/glossary/terms/${id}`, { sortOrder }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/glossary"] });
    },
  });

  async function handleGlossaryPreview(file: File): Promise<NormalizedCsvPreview> {
    const formData = new FormData();
    formData.append("file", file);
    const data = await apiRequest("POST", "/api/glossary/import/preview", formData) as ImportPreview;
    setImportPreview(data);
    return {
      errors: data.errors,
      staleWarning: data.staleWarning,
      deletionWarning: data.deletionThresholdWarning,
      hasChanges:
        data.newParents.length > 0 ||
        data.updatedParents.length > 0 ||
        data.deletedParents.length > 0 ||
        data.newChildren.length > 0 ||
        data.updatedChildren.length > 0 ||
        data.movedChildren.length > 0 ||
        data.deletedChildren.length > 0,
    };
  }

  async function handleGlossaryCommit(file: File): Promise<void> {
    const formData = new FormData();
    formData.append("file", file);
    const data = await apiRequest("POST", "/api/glossary/import/commit", formData) as ImportResult;
    queryClient.invalidateQueries({ queryKey: ["/api/glossary"] });
    queryClient.invalidateQueries({ queryKey: ["/api/glossary/stats"] });
    const parts = [];
    if (data.parentsCreated > 0) parts.push(`${data.parentsCreated} concepts created`);
    if (data.parentsUpdated > 0) parts.push(`${data.parentsUpdated} concepts updated`);
    if (data.parentsDeleted > 0) parts.push(`${data.parentsDeleted} concepts deleted`);
    if (data.childrenCreated > 0) parts.push(`${data.childrenCreated} entries created`);
    if (data.childrenUpdated > 0) parts.push(`${data.childrenUpdated} entries updated`);
    if (data.childrenMoved > 0) parts.push(`${data.childrenMoved} entries moved`);
    if (data.childrenDeleted > 0) parts.push(`${data.childrenDeleted} entries deleted`);
    toast({
      title: "Import complete",
      description: parts.length > 0 ? parts.join(", ") : "No changes were needed",
    });
  }

  function glossaryDiffSummary(_normalized: NormalizedCsvPreview) {
    if (!importPreview) return null;
    if (!_normalized.hasChanges) {
      return (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2" data-testid="import-no-changes">
          <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
          No changes detected. The glossary is already up to date.
        </div>
      );
    }
    const p = importPreview;
    return (
      <>
        <h4 className="text-sm font-medium">Changes to apply:</h4>
        {p.newParents.length > 0 && (
          <div className="text-sm" data-testid="import-new-parents">
            <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400 font-medium">
              <Plus className="w-3 h-3" />
              {p.newParents.length} new concept{p.newParents.length === 1 ? "" : "s"}
            </span>
            <span className="text-muted-foreground ml-2">
              {p.newParents.map(p2 => p2.term).join(", ")}
            </span>
          </div>
        )}
        {p.updatedParents.length > 0 && (
          <div className="text-sm" data-testid="import-updated-parents">
            <span className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-400 font-medium">
              <Pencil className="w-3 h-3" />
              {p.updatedParents.length} updated concept{p.updatedParents.length === 1 ? "" : "s"}
            </span>
            <span className="text-muted-foreground ml-2">
              {p.updatedParents.map(p2 => p2.term).join(", ")}
            </span>
          </div>
        )}
        {p.deletedParents.length > 0 && (
          <div className="text-sm" data-testid="import-deleted-parents">
            <span className="inline-flex items-center gap-1 text-destructive font-medium">
              <Trash2 className="w-3 h-3" />
              {p.deletedParents.length} deleted concept{p.deletedParents.length === 1 ? "" : "s"}
            </span>
            <span className="text-muted-foreground ml-2">
              {p.deletedParents.map(p2 => p2.term).join(", ")}
            </span>
          </div>
        )}
        {p.newChildren.length > 0 && (
          <div className="text-sm" data-testid="import-new-children">
            <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400 font-medium">
              <Plus className="w-3 h-3" />
              {p.newChildren.length} new entr{p.newChildren.length === 1 ? "y" : "ies"}
            </span>
            <span className="text-muted-foreground ml-2">
              {p.newChildren.slice(0, 5).map(c => c.value).join(", ")}
              {p.newChildren.length > 5 && ` +${p.newChildren.length - 5} more`}
            </span>
          </div>
        )}
        {p.updatedChildren.length > 0 && (
          <div className="text-sm" data-testid="import-updated-children">
            <span className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-400 font-medium">
              <Pencil className="w-3 h-3" />
              {p.updatedChildren.length} updated entr{p.updatedChildren.length === 1 ? "y" : "ies"}
            </span>
            <span className="text-muted-foreground ml-2">
              {p.updatedChildren.slice(0, 5).map(c => c.value).join(", ")}
              {p.updatedChildren.length > 5 && ` +${p.updatedChildren.length - 5} more`}
            </span>
          </div>
        )}
        {p.movedChildren.length > 0 && (
          <div className="text-sm" data-testid="import-moved-children">
            <span className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400 font-medium">
              <ArrowRightLeft className="w-3 h-3" />
              {p.movedChildren.length} moved entr{p.movedChildren.length === 1 ? "y" : "ies"}
            </span>
            <span className="text-muted-foreground ml-2">
              {p.movedChildren.slice(0, 3).map(c => `${c.value}: ${c.oldParentTerm} → ${c.newParentTerm}`).join("; ")}
              {p.movedChildren.length > 3 && ` +${p.movedChildren.length - 3} more`}
            </span>
          </div>
        )}
        {p.deletedChildren.length > 0 && (
          <div className="text-sm" data-testid="import-deleted-children">
            <span className="inline-flex items-center gap-1 text-destructive font-medium">
              <Trash2 className="w-3 h-3" />
              {p.deletedChildren.length} deleted entr{p.deletedChildren.length === 1 ? "y" : "ies"}
            </span>
            <span className="text-muted-foreground ml-2">
              {p.deletedChildren.slice(0, 5).map(c => c.value).join(", ")}
              {p.deletedChildren.length > 5 && ` +${p.deletedChildren.length - 5} more`}
            </span>
          </div>
        )}
      </>
    );
  }

  const filteredTerms = useMemo(() => {
    const terms = glossaryQuery.data?.terms || [];
    const filtered = terms.filter((t) => {
      if (!showInactive && !t.isActive) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const parentMatch =
          t.term.toLowerCase().includes(q) ||
          t.definition.toLowerCase().includes(q) ||
          t.category.toLowerCase().includes(q);
        const childMatch = t.children.some(
          (c) =>
            c.value.toLowerCase().includes(q) ||
            c.definition.toLowerCase().includes(q) ||
            c.aliases.some((a) => a.toLowerCase().includes(q))
        );
        return parentMatch || childMatch;
      }
      if (categoryFilter !== "all" && t.category !== categoryFilter) return false;
      return true;
    });
    if (sortOrder === "az") {
      return [...filtered].sort((a, b) => a.term.localeCompare(b.term));
    }
    return filtered;
  }, [glossaryQuery.data, searchTerm, categoryFilter, showInactive, sortOrder]);

  function toggleExpanded(id: string) {
    setExpandedTerms((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function openAddTerm() {
    setEditingTerm(null);
    setTermForm({ term: "", category: "score", definition: "", observedAtLocations: [] });
    setTermDialogOpen(true);
  }

  if (addGroupRef) {
    addGroupRef.current = openAddTerm;
  }

  if (actionsRef) {
    actionsRef.current = {
      openInstructions: () => setCsvHelpOpen(true),
      exportCsv: () => {
        const terms = glossaryQuery.data?.terms;
        if (terms && terms.length > 0) exportGlossaryCsv(terms);
      },
      importCsv: () => setImportDialogOpen(true),
      canExport: !!glossaryQuery.data?.terms?.length,
    };
  }

  function openEditTerm(t: GlossaryTerm) {
    setEditingTerm(t);
    setTermForm({
      term: t.term,
      category: t.category,
      definition: t.definition,
      observedAtLocations: t.observedAtLocations || [],
    });
    setTermDialogOpen(true);
  }

  function handleTermSubmit() {
    if (!termForm.term.trim() || !termForm.definition.trim()) return;
    if (editingTerm) {
      updateTermMutation.mutate({ id: editingTerm.id, data: termForm });
    } else {
      createTermMutation.mutate(termForm);
    }
  }

  function openAddChild(parentId: string) {
    setEditingChild(null);
    setChildParentId(parentId);
    setChildForm({ value: "", aliases: [], definition: "", spokenForm: "", observedAtLocations: [] });
    setChildDialogOpen(true);
  }

  function openEditChild(child: GlossaryChild) {
    setEditingChild(child);
    setChildParentId(child.parentId);
    setChildForm({
      value: child.value,
      aliases: child.aliases || [],
      definition: child.definition || "",
      spokenForm: child.spokenForm || "",
      observedAtLocations: child.observedAtLocations || [],
    });
    setChildDialogOpen(true);
  }

  function handleChildSubmit() {
    if (!childForm.value.trim()) return;
    if (editingChild) {
      updateChildMutation.mutate({ id: editingChild.id, data: childForm });
    } else {
      createChildMutation.mutate({ ...childForm, parentId: childParentId });
    }
  }

  function handleReorderTerm(terms: GlossaryTerm[], index: number, direction: "up" | "down") {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= terms.length) return;
    const current = terms[index];
    const target = terms[targetIndex];
    updateTermSortMutation.mutate({ id: current.id, sortOrder: target.sortOrder });
    updateTermSortMutation.mutate({ id: target.id, sortOrder: current.sortOrder });
  }

  function handleReorderChild(children: GlossaryChild[], index: number, direction: "up" | "down") {
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= children.length) return;
    const current = children[index];
    const target = children[targetIndex];
    updateChildMutation.mutate({ id: current.id, data: { sortOrder: target.sortOrder } });
    updateChildMutation.mutate({ id: target.id, data: { sortOrder: current.sortOrder } });
  }

  const stats = statsQuery.data;
  const allTerms = glossaryQuery.data?.terms || [];
  const isEmpty = allTerms.length === 0 && !glossaryQuery.isLoading;
  const isFiltered = searchTerm !== "" || categoryFilter !== "all";

  const hasActiveFilters = searchTerm !== "" || sortOrder !== "display" || showInactive;
  const clearFilters = () => {
    setSearchTerm("");
    setSortOrder("display");
    setShowInactive(false);
  };

  const filterDescription = useMemo(() => {
    const parts: string[] = [];
    if (searchTerm) parts.push(`matching "${searchTerm}"`);
    if (categoryFilter !== "all") {
      const catLabel = categories.find(c => c.value === categoryFilter)?.label || categoryFilter;
      parts.push(`in ${catLabel}`);
    }
    if (showInactive) parts.push("including inactive");
    if (sortOrder === "az") parts.push("sorted A–Z");
    if (parts.length === 0) {
      const catLabel = categoryFilter === "all" ? "all categories" : (categories.find(c => c.value === categoryFilter)?.label || categoryFilter);
      return `Glossary concepts across ${catLabel}.`;
    }
    return `Showing concepts ${parts.join(", ")}.`;
  }, [searchTerm, categoryFilter, showInactive, sortOrder, categories]);

  if (glossaryQuery.isLoading) {
    return (
      <div className="space-y-4" data-testid="glossary-loading">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between gap-2">
              <div className="space-y-2">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-32" />
              </div>
              <Skeleton className="h-6 w-16" />
            </CardHeader>
          </Card>
        ))}
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div className="flex flex-col items-center justify-center py-16 space-y-4" data-testid="glossary-empty">
        <BookOpen className="w-12 h-12 text-muted-foreground" />
        <h3 className="text-lg font-medium">No glossary concepts yet</h3>
        <p className="text-sm text-muted-foreground text-center max-w-md">
          The domain glossary teaches the AI how shelter staff talk. Seed the defaults to get started, or create your first concept.
        </p>
        <div className="flex gap-2">
          <Button onClick={() => seedMutation.mutate()} disabled={seedMutation.isPending} data-testid="button-seed-glossary">
            <Sprout className="w-4 h-4 mr-2" />
            {seedMutation.isPending ? "Seeding..." : "Seed Defaults"}
          </Button>
          <Button variant="outline" onClick={openAddTerm} data-testid="button-add-term-empty">
            <Plus className="w-4 h-4 mr-2" />
            Add Concept
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Card data-testid="glossary-filter-card">
        <CardContent className="p-4">
          <CollapsibleFilterHeader
            collapsed={filterCollapsed}
            onToggle={() => setFilterCollapsed(c => !c)}
            description={filterDescription}
            hasActiveFilters={hasActiveFilters}
            onClearFilters={clearFilters}
            descriptionTestId="text-glossary-filter-description"
            toggleTestId="button-toggle-glossary-filter"
            clearTestId="button-clear-glossary-filters"
            alwaysVisibleContent={
              stats ? (
                <span className="text-sm text-muted-foreground" data-testid="text-glossary-stats">
                  {stats.activeParents} concepts, {stats.activeChildren} entries
                </span>
              ) : undefined
            }
          >
            <div className="flex flex-wrap items-center gap-4 mt-3">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search concepts, entries, aliases..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="input-glossary-search"
                />
              </div>
              <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as "display" | "az")}>
                <SelectTrigger className="w-[150px]" data-testid="select-sort-order">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="display" data-testid="option-sort-display">Display Order</SelectItem>
                  <SelectItem value="az" data-testid="option-sort-az">A – Z</SelectItem>
                </SelectContent>
              </Select>
              <div className="flex items-center gap-2">
                <Switch
                  id="show-inactive"
                  checked={showInactive}
                  onCheckedChange={setShowInactive}
                  data-testid="switch-show-inactive"
                />
                <Label className="text-sm text-muted-foreground cursor-pointer" htmlFor="show-inactive">
                  Show inactive
                </Label>
              </div>
            </div>
          </CollapsibleFilterHeader>
        </CardContent>
      </Card>

      {filteredTerms.length === 0 && (
        <div className="text-center py-12 text-muted-foreground" data-testid="glossary-no-results">
          <Search className="w-8 h-8 mx-auto mb-3 opacity-50" />
          <p>No concepts match your filters</p>
        </div>
      )}

      <div className="space-y-2">
        {filteredTerms.map((term, termIndex) => {
          const isExpanded = expandedTerms.has(term.id);
          const visibleChildren = showInactive ? term.children : term.children.filter((c) => c.isActive);
          const categoryLabel = categories.find((c) => c.value === term.category)?.label || term.category;

          return (
            <Card
              key={term.id}
              className={!term.isActive ? "opacity-60" : ""}
              data-testid={`card-term-${term.id}`}
            >
              <Collapsible open={isExpanded} onOpenChange={() => toggleExpanded(term.id)}>
                <CollapsibleTrigger asChild>
                  <CardHeader className="cursor-pointer flex flex-row items-center justify-between gap-2 py-3">
                    <div className="flex items-center gap-2 flex-wrap min-w-0">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className={`inline-block w-2 h-2 rounded-full shrink-0 ${term.isActive ? "bg-green-500" : "bg-muted-foreground/40"}`}
                            data-testid={`indicator-term-active-${term.id}`}
                          />
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p>{term.isActive ? "Concept active — included in AI prompts" : "Concept inactive — excluded from AI prompts"}</p>
                        </TooltipContent>
                      </Tooltip>
                      <span className="text-xs text-muted-foreground shrink-0">{termIndex + 1}.</span>
                      <span className="font-medium" data-testid={`text-term-name-${term.id}`}>
                        <HighlightText text={term.term} search={searchTerm} />
                      </span>
                      <Badge variant="outline" className="text-xs no-default-hover-elevate no-default-active-elevate" data-testid={`badge-category-${term.id}`}>
                        {categoryLabel}
                      </Badge>
                      <Badge variant="secondary" className="text-xs no-default-hover-elevate no-default-active-elevate" data-testid={`badge-child-count-${term.id}`}>
                        {term.children.length} {term.children.length === 1 ? "entry" : "entries"}
                      </Badge>
                      {!term.isActive && !isExpanded && (
                        <Badge variant="outline" className="text-xs text-muted-foreground no-default-hover-elevate no-default-active-elevate">
                          Inactive
                        </Badge>
                      )}
                      {isExpanded && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="flex items-center gap-2 shrink-0" onClick={(e) => e.stopPropagation()}>
                              <Label className="text-xs font-medium text-muted-foreground cursor-help shrink-0">Concept Status:</Label>
                              <Switch
                                checked={term.isActive}
                                onCheckedChange={(checked) =>
                                  updateTermMutation.mutate({ id: term.id, data: { isActive: checked } })
                                }
                                data-testid={`switch-term-active-${term.id}`}
                              />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" className="max-w-xs">
                            <p>Controls whether this entire concept and all its entries are sent to BETI when generating AI responses.</p>
                          </TooltipContent>
                        </Tooltip>
                      )}
                      {term.observedAtLocations && term.observedAtLocations.length > 0 && (
                        <div className="hidden md:flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-muted-foreground" />
                          <LocationBadges locations={term.observedAtLocations} />
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <ExpandCollapseButton
                        expanded={isExpanded}
                        pointerEvents={false}
                        tabIndex={-1}
                        testId={`button-expand-term-${term.id}`}
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button size="icon" variant="ghost" data-testid={`button-term-actions-${term.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); handleReorderTerm(filteredTerms, termIndex, "up"); }}
                            disabled={termIndex === 0 || isFiltered}
                            data-testid={`menu-reorder-term-up-${term.id}`}
                          >
                            <ArrowUp className="w-4 h-4 mr-2" />
                            Move up
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); handleReorderTerm(filteredTerms, termIndex, "down"); }}
                            disabled={termIndex === filteredTerms.length - 1 || isFiltered}
                            data-testid={`menu-reorder-term-down-${term.id}`}
                          >
                            <ArrowDown className="w-4 h-4 mr-2" />
                            Move down
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => { e.stopPropagation(); openEditTerm(term); }}
                            data-testid={`menu-edit-term-${term.id}`}
                          >
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit details
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setChangeCategoryTarget({ termId: term.id, termName: term.term, currentCategory: term.category });
                              setChangeCategorySelected("");
                            }}
                            data-testid={`menu-change-category-${term.id}`}
                          >
                            <Tag className="w-4 h-4 mr-2" />
                            Edit category
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget({
                                type: "term",
                                id: term.id,
                                label: term.term,
                                hasChildren: term.children.length,
                              });
                            }}
                            className="text-destructive focus:text-destructive"
                            data-testid={`menu-delete-term-${term.id}`}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </CardHeader>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <CardContent className="pt-0 space-y-3 bg-muted/40 rounded-b-md mx-0 -mb-1 pb-4">
                    <div className="border-l-2 border-muted-foreground/30 pl-3 py-1" data-testid={`text-term-definition-${term.id}`}>
                      <p className="text-sm italic text-muted-foreground">
                        <HighlightText text={term.definition} search={searchTerm} />
                      </p>
                    </div>
                    {term.observedAtLocations && term.observedAtLocations.length > 0 && (
                      <div className="flex items-center gap-2 md:hidden">
                        <MapPin className="w-3 h-3 text-muted-foreground" />
                        <LocationBadges locations={term.observedAtLocations} />
                      </div>
                    )}

                    {visibleChildren.length === 0 && (
                      <p className="text-sm text-muted-foreground italic">No entries defined</p>
                    )}

                    <div className="space-y-1">
                      {visibleChildren.map((child, childIndex) => {
                        const childMatches = searchTerm && (
                          child.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          child.definition.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          child.aliases.some((a) => a.toLowerCase().includes(searchTerm.toLowerCase()))
                        );

                        return (
                          <div
                            key={child.id}
                            className={`flex items-start justify-between gap-2 p-3 rounded-md border bg-card ${
                              !child.isActive ? "opacity-50" : ""
                            } ${childMatches ? "ring-1 ring-yellow-400 dark:ring-yellow-600" : ""}`}
                            data-testid={`row-child-${child.id}`}
                          >
                            <div className="flex-1 min-w-0 space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-mono text-sm font-medium" data-testid={`text-child-value-${child.id}`}>
                                  <HighlightText text={child.value} search={searchTerm} />
                                </span>
                                {!child.isActive && (
                                  <Badge variant="outline" className="text-xs text-muted-foreground no-default-hover-elevate no-default-active-elevate">
                                    Inactive
                                  </Badge>
                                )}
                              </div>
                              {child.definition && (
                                <div className="border-l-2 border-muted-foreground/30 pl-3 py-1" data-testid={`text-child-definition-${child.id}`}>
                                  <p className="text-sm italic text-muted-foreground">
                                    <HighlightText text={child.definition} search={searchTerm} />
                                  </p>
                                </div>
                              )}
                              <TermExtras child={child} searchTerm={searchTerm} />
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span
                                    className={`inline-block w-2 h-2 rounded-full shrink-0 ${child.isActive ? "bg-green-500" : "bg-muted-foreground/40"}`}
                                    data-testid={`indicator-child-active-${child.id}`}
                                  />
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs">
                                  <p>{child.isActive ? "Entry active — visible to users and included in AI prompts" : "Entry inactive — hidden from users and excluded from AI prompts"}</p>
                                </TooltipContent>
                              </Tooltip>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="icon" variant="ghost" data-testid={`button-child-actions-${child.id}`}>
                                    <MoreVertical className="w-4 h-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onClick={() => handleReorderChild(visibleChildren, childIndex, "up")}
                                    disabled={childIndex === 0 || isFiltered}
                                    data-testid={`menu-reorder-child-up-${child.id}`}
                                  >
                                    <ArrowUp className="w-4 h-4 mr-2" />
                                    Move up
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => handleReorderChild(visibleChildren, childIndex, "down")}
                                    disabled={childIndex === visibleChildren.length - 1 || isFiltered}
                                    data-testid={`menu-reorder-child-down-${child.id}`}
                                  >
                                    <ArrowDown className="w-4 h-4 mr-2" />
                                    Move down
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() =>
                                      updateChildMutation.mutate({ id: child.id, data: { isActive: !child.isActive } })
                                    }
                                    data-testid={`menu-toggle-child-active-${child.id}`}
                                  >
                                    {child.isActive ? (
                                      <><EyeOff className="w-4 h-4 mr-2" />Deactivate entry</>
                                    ) : (
                                      <><Eye className="w-4 h-4 mr-2" />Activate entry</>
                                    )}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => {
                                      setMoveChildTarget({ childId: child.id, childValue: child.value, currentParentId: term.id });
                                      setMoveChildSelectedParent("");
                                    }}
                                    data-testid={`menu-move-child-${child.id}`}
                                  >
                                    <FolderInput className="w-4 h-4 mr-2" />
                                    Change concept
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onClick={() => openEditChild(child)}
                                    data-testid={`menu-edit-child-${child.id}`}
                                  >
                                    <Pencil className="w-4 h-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setDeleteTarget({ type: "child", id: child.id, label: child.value })
                                    }
                                    className="text-destructive focus:text-destructive"
                                    data-testid={`menu-delete-child-${child.id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openAddChild(term.id)}
                      data-testid={`button-add-child-${term.id}`}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Entry
                    </Button>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          );
        })}
      </div>

      <Dialog open={termDialogOpen} onOpenChange={setTermDialogOpen}>
        <DialogContent data-testid="dialog-term-form">
          <DialogHeader>
            <DialogTitle>{editingTerm ? "Edit Concept" : "Add Concept"}</DialogTitle>
            <DialogDescription>
              {editingTerm
                ? "Update this glossary concept. A concept represents something like 'Behavior Score' or 'Kennel Status'."
                : "Create a new glossary concept. A concept has specific entries (codes or values) under it."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Concept Name</Label>
              <Input
                value={termForm.term}
                onChange={(e) => setTermForm({ ...termForm, term: e.target.value })}
                placeholder="e.g., Behavior Score"
                data-testid="input-term-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Category</Label>
              <Select
                value={termForm.category}
                onValueChange={(val) => setTermForm({ ...termForm, category: val })}
              >
                <SelectTrigger data-testid="select-term-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value} data-testid={`option-term-category-${cat.value}`}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Definition</Label>
              <Textarea
                value={termForm.definition}
                onChange={(e) => setTermForm({ ...termForm, definition: e.target.value })}
                placeholder="What does this term mean to shelter staff?"
                data-testid="input-term-definition"
              />
            </div>
            <div className="space-y-2">
              <Label>Observed at Locations (optional)</Label>
              <LocationSelect
                values={termForm.observedAtLocations}
                onChange={(vals) => setTermForm({ ...termForm, observedAtLocations: vals })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTermDialogOpen(false)} data-testid="button-cancel-term">
              Cancel
            </Button>
            <Button
              onClick={handleTermSubmit}
              disabled={!termForm.term.trim() || !termForm.definition.trim() || createTermMutation.isPending || updateTermMutation.isPending}
              data-testid="button-save-term"
            >
              {(createTermMutation.isPending || updateTermMutation.isPending) ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={childDialogOpen} onOpenChange={setChildDialogOpen}>
        <DialogContent data-testid="dialog-child-form">
          <DialogHeader>
            <DialogTitle>{editingChild ? "Edit Entry" : "Add Entry"}</DialogTitle>
            <DialogDescription>
              {editingChild
                ? "Update this glossary entry. An entry is a specific code or value that staff use."
                : "Add a new entry to this concept. An entry is a specific code or value (like '4' or 'UNAVAIL_BEHAVIOR')."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Value</Label>
              <Input
                value={childForm.value}
                onChange={(e) => setChildForm({ ...childForm, value: e.target.value })}
                placeholder="e.g., 4, UNAVAIL_BEHAVIOR, SA"
                data-testid="input-child-value"
              />
            </div>
            <div className="space-y-2">
              <Label>Aliases</Label>
              <TagInput
                values={childForm.aliases}
                onChange={(vals) => setChildForm({ ...childForm, aliases: vals })}
                placeholder="Type an alias, then press Enter or comma"
              />
              <p className="text-xs text-muted-foreground">Other names staff use for this entry. The AI will recognize these as equivalent.</p>
            </div>
            <div className="space-y-2">
              <Label>Definition</Label>
              <Textarea
                value={childForm.definition}
                onChange={(e) => setChildForm({ ...childForm, definition: e.target.value })}
                placeholder="What does this value mean?"
                data-testid="input-child-definition"
              />
            </div>
            <div className="space-y-2">
              <Label>Spoken Form (optional)</Label>
              <Input
                value={childForm.spokenForm}
                onChange={(e) => setChildForm({ ...childForm, spokenForm: e.target.value })}
                placeholder='e.g., "a four", "behavior hold"'
                data-testid="input-child-spoken-form"
              />
            </div>
            <div className="space-y-2">
              <Label>Observed at Locations (optional)</Label>
              <LocationSelect
                values={childForm.observedAtLocations}
                onChange={(vals) => setChildForm({ ...childForm, observedAtLocations: vals })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChildDialogOpen(false)} data-testid="button-cancel-child">
              Cancel
            </Button>
            <Button
              onClick={handleChildSubmit}
              disabled={!childForm.value.trim() || createChildMutation.isPending || updateChildMutation.isPending}
              data-testid="button-save-child"
            >
              {(createChildMutation.isPending || updateChildMutation.isPending) ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="dialog-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {deleteTarget?.type === "term" ? "Concept" : "Entry"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.type === "term" && deleteTarget.hasChildren ? (
                <>
                  This will permanently delete the <strong>{deleteTarget.label}</strong> concept and all{" "}
                  <strong>{deleteTarget.hasChildren}</strong> of its entries. This action cannot be undone.
                </>
              ) : (
                <>
                  This will permanently delete <strong>{deleteTarget?.label}</strong>. This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (!deleteTarget) return;
                if (deleteTarget.type === "term") {
                  deleteTermMutation.mutate(deleteTarget.id);
                } else {
                  deleteChildMutation.mutate(deleteTarget.id);
                }
              }}
              data-testid="button-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={csvHelpOpen} onOpenChange={setCsvHelpOpen}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto" data-testid="dialog-csv-help">
          <DialogHeader>
            <DialogTitle>Editing the Glossary via Spreadsheet</DialogTitle>
            <DialogDescription>
              How to use Export and Import to manage concepts and entries in Google Sheets or Excel.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-sm">
            <div className="space-y-2">
              <h4 className="font-medium">How the glossary is structured</h4>
              <p className="text-muted-foreground">
                <strong>Categories</strong> are tags that classify concepts (e.g., "Score", "Status", "Zone").
                <strong> Concepts</strong> are things like "Behavior Score" or "Kennel Status".
                <strong> Entries</strong> are the specific codes or values under each concept (e.g., "4", "UNAVAIL_BEHAVIOR").
                In the CSV, each row represents one entry, and the concept info is repeated on every row that belongs to it.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Overview</h4>
              <p className="text-muted-foreground">
                You can bulk-edit the glossary by exporting it to a CSV file, opening it in
                Google Sheets or Excel, making your changes, then importing the updated file.
                The import is a <strong>full replacement</strong> — the spreadsheet becomes the
                new glossary.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Step-by-step</h4>
              <ol className="list-decimal list-inside space-y-1.5 text-muted-foreground">
                <li>Click <strong>Export CSV</strong> to download the current glossary.</li>
                <li>Open the file in Google Sheets or Excel.</li>
                <li>Make your changes (see below).</li>
                <li>Download / Save As a <strong>.csv</strong> file.</li>
                <li>Click <strong>Import CSV</strong> and select the file.</li>
                <li>Review the change summary, then click <strong>Import</strong> to apply.</li>
              </ol>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Editing existing entries</h4>
              <p className="text-muted-foreground">
                Each row is one entry under a concept. Edit any cell directly.
                Keep the <strong>parent_id</strong> and <strong>child_id</strong> columns
                intact — they tell the system which records to update.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Adding new entries or concepts</h4>
              <p className="text-muted-foreground">
                Add a new row at the bottom of a concept. Leave the <strong>parent_id</strong> and
                <strong> child_id</strong> columns blank for new records. Fill in
                the <strong>term</strong> (concept name), <strong>category</strong>, <strong>parent_definition</strong>,
                and <strong>value</strong> (entry code) columns at minimum.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Adding new categories</h4>
              <p className="text-muted-foreground">
                Before importing a CSV with a new category, you must first create it using the
                <strong> Categories</strong> button on the glossary toolbar. The import validates
                every row's category against the list of categories you've defined. Unknown categories
                will be rejected with an error message.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Deleting entries or concepts</h4>
              <p className="text-muted-foreground">
                Delete the entire row from the spreadsheet. When you import, any entries or concepts that were
                in the glossary but are no longer in the file will be removed. If you delete more
                than half the glossary, you'll see a confirmation prompt before the import proceeds.
              </p>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Column reference</h4>
              <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-muted-foreground">
                <span className="font-mono text-xs">term</span>
                <span>Concept name (e.g., "Behavior Score")</span>
                <span className="font-mono text-xs">category</span>
                <span>Category tag slug (must match one defined via the Categories button)</span>
                <span className="font-mono text-xs">parent_definition</span>
                <span>Description of the concept</span>
                <span className="font-mono text-xs">value</span>
                <span>The specific entry code (e.g., "4", "UNAVAIL_BEHAVIOR")</span>
                <span className="font-mono text-xs">aliases</span>
                <span>Alternative names, separated by semicolons</span>
                <span className="font-mono text-xs">child_definition</span>
                <span>What this specific entry means</span>
                <span className="font-mono text-xs">spoken_form</span>
                <span>How staff say it out loud</span>
                <span className="font-mono text-xs">parent_active / child_active</span>
                <span>true or false — inactive items are hidden from AI</span>
                <span className="font-mono text-xs">parent_locations / child_locations</span>
                <span>Location codes separated by semicolons (e.g., BO;SA). Leave blank for all.</span>
              </div>
            </div>

            <div className="space-y-2">
              <h4 className="font-medium">Tips</h4>
              <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                <li>Don't delete or rename the header row.</li>
                <li>Don't modify values in the <strong>parent_id</strong> or <strong>child_id</strong> columns — those are internal IDs.</li>
                <li>Reorder rows to change sort order — the order in the spreadsheet becomes the display order.</li>
                <li>Use semicolons (not commas) to separate multiple aliases or locations.</li>
                <li>Google Sheets may show TRUE/FALSE in uppercase — that's fine, the import handles it.</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setCsvHelpOpen(false)} data-testid="button-close-csv-help">
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CsvImportDialog
        open={importDialogOpen}
        onOpenChange={(open) => {
          setImportDialogOpen(open);
          if (!open) setImportPreview(null);
        }}
        title="Import Glossary CSV"
        description="Upload a CSV file to replace the entire glossary. This is a full replacement — any concepts or entries not in the CSV will be deleted."
        onPreview={handleGlossaryPreview}
        onCommit={handleGlossaryCommit}
        diffSummary={glossaryDiffSummary}
      />

      <Dialog open={categoryMgmtOpen} onOpenChange={setCategoryMgmtOpen}>
        <DialogContent className="max-w-lg p-6">
          <DialogHeader>
            <DialogTitle>Manage Categories</DialogTitle>
            <DialogDescription>
              Categories are tags that classify concepts (e.g., "Score", "Status", "Zone"). Each concept is assigned one category. Categories in use by existing concepts cannot be deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {categories.length === 0 && (
                <p className="text-sm text-muted-foreground">No categories yet.</p>
              )}
              {categories.map((cat) => (
                <div key={cat.value} className="py-2 border-b last:border-b-0">
                  {editingCatSlug === cat.value ? (
                    <div className="space-y-2">
                      <div className="flex items-end gap-2">
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs text-muted-foreground">Label</Label>
                          <Input
                            value={editCatLabel}
                            onChange={(e) => setEditCatLabel(e.target.value)}
                            data-testid={`input-edit-category-label-${cat.value}`}
                          />
                        </div>
                        <div className="flex-1 space-y-1">
                          <Label className="text-xs text-muted-foreground">Slug</Label>
                          <Input
                            value={editCatSlug}
                            onChange={(e) => setEditCatSlug(e.target.value)}
                            data-testid={`input-edit-category-slug-${cat.value}`}
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          onClick={() => updateCategoryMutation.mutate({
                            originalSlug: cat.value,
                            label: editCatLabel,
                            slug: editCatSlug !== cat.value ? editCatSlug : undefined,
                          })}
                          disabled={!editCatLabel.trim() || !editCatSlug.trim() || updateCategoryMutation.isPending}
                          data-testid={`button-save-category-${cat.value}`}
                        >
                          {updateCategoryMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingCatSlug(null)}
                          data-testid={`button-cancel-edit-category-${cat.value}`}
                        >
                          Cancel
                        </Button>
                        {editCatSlug !== cat.value && (
                          <span className="text-xs text-muted-foreground">
                            Slug change will update {cat.groupCount} concept{cat.groupCount !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="flex flex-col min-w-0">
                          <span className="text-sm font-medium truncate" data-testid={`text-category-label-${cat.value}`}>{cat.label}</span>
                          <span className="text-xs text-muted-foreground" data-testid={`text-category-slug-${cat.value}`}>{cat.value}</span>
                        </div>
                        <Badge variant="secondary" data-testid={`badge-category-count-${cat.value}`}>
                          {cat.groupCount} concept{cat.groupCount !== 1 ? "s" : ""}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => {
                            setEditingCatSlug(cat.value);
                            setEditCatLabel(cat.label);
                            setEditCatSlug(cat.value);
                          }}
                          data-testid={`button-edit-category-${cat.value}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => deleteCategoryMutation.mutate(cat.value)}
                          disabled={deleteCategoryMutation.isPending || cat.groupCount > 0}
                          data-testid={`button-delete-category-${cat.value}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t pt-4 space-y-2">
              <Label>Add new category</Label>
              <div className="flex items-end gap-2">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-muted-foreground">Label</Label>
                  <Input
                    placeholder="e.g. Medical"
                    value={newCatLabel}
                    onChange={(e) => {
                      setNewCatLabel(e.target.value);
                      if (!newCatSlug || newCatSlug === newCatLabel.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "")) {
                        setNewCatSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, ""));
                      }
                    }}
                    data-testid="input-new-category-label"
                  />
                </div>
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-muted-foreground">Slug</Label>
                  <Input
                    placeholder="e.g. medical"
                    value={newCatSlug}
                    onChange={(e) => setNewCatSlug(e.target.value)}
                    data-testid="input-new-category-slug"
                  />
                </div>
                <Button
                  onClick={() => addCategoryMutation.mutate({ slug: newCatSlug, label: newCatLabel })}
                  disabled={!newCatSlug.trim() || !newCatLabel.trim() || addCategoryMutation.isPending}
                  data-testid="button-add-category"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!moveChildTarget} onOpenChange={(open) => { if (!open) { setMoveChildTarget(null); setMoveChildSelectedParent(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Move entry to another concept</DialogTitle>
            <DialogDescription>
              Move <strong>{moveChildTarget?.childValue}</strong> to a different parent concept.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Select target concept</Label>
            <Select value={moveChildSelectedParent} onValueChange={setMoveChildSelectedParent}>
              <SelectTrigger data-testid="select-move-child-target">
                <SelectValue placeholder="Choose a concept..." />
              </SelectTrigger>
              <SelectContent>
                {allTerms
                  .filter((t) => t.id !== moveChildTarget?.currentParentId)
                  .map((t) => (
                    <SelectItem key={t.id} value={t.id} data-testid={`option-move-target-${t.id}`}>
                      {t.term} ({categories.find((c) => c.value === t.category)?.label || t.category})
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMoveChildTarget(null); setMoveChildSelectedParent(""); }} data-testid="button-cancel-move-child">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (moveChildTarget && moveChildSelectedParent) {
                  moveChildMutation.mutate({ childId: moveChildTarget.childId, newParentId: moveChildSelectedParent });
                }
              }}
              disabled={!moveChildSelectedParent || moveChildMutation.isPending}
              data-testid="button-confirm-move-child"
            >
              {moveChildMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FolderInput className="w-4 h-4 mr-2" />}
              Move Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!changeCategoryTarget} onOpenChange={(open) => { if (!open) { setChangeCategoryTarget(null); setChangeCategorySelected(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change concept category</DialogTitle>
            <DialogDescription>
              Reassign <strong>{changeCategoryTarget?.termName}</strong> to a different category.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Select new category</Label>
            <Select value={changeCategorySelected} onValueChange={setChangeCategorySelected}>
              <SelectTrigger data-testid="select-change-category-target">
                <SelectValue placeholder="Choose a category..." />
              </SelectTrigger>
              <SelectContent>
                {categories
                  .filter((c) => c.value !== changeCategoryTarget?.currentCategory)
                  .map((c) => (
                    <SelectItem key={c.value} value={c.value} data-testid={`option-change-cat-${c.value}`}>
                      {c.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setChangeCategoryTarget(null); setChangeCategorySelected(""); }} data-testid="button-cancel-change-category">
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (changeCategoryTarget && changeCategorySelected) {
                  changeCategoryMutation.mutate({ termId: changeCategoryTarget.termId, newCategory: changeCategorySelected });
                }
              }}
              disabled={!changeCategorySelected || changeCategoryMutation.isPending}
              data-testid="button-confirm-change-category"
            >
              {changeCategoryMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Tag className="w-4 h-4 mr-2" />}
              Change Category
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
